import { makeAutoObservable, runInAction } from 'mobx';
import { HelperCore } from '@src/common/helper';
import Other from '@src/common/helper/other';
import { request } from '@src/common';
import globalStore from './global';
import Loader from '@src/common/helper/loader';
import EE from '@src/common/event';
import Conf from '@src/common/conf';
import { reportToNRobot } from '@src/common/request';
import AgreeModal from '@/components/modal/agree';
import { sendBroadcast } from '@/hooks/useBroadcast';
import Local from '@/common/helper/local';
import slsLogger from '@public/log/sls';
import { message } from 'antd';
import client from './client';
import { cookie } from '@q/solib-utils';
import { loadModule } from '@/common/loadModule';
import { getLoginInfo } from '@/common/login-info';
import { refreshUserCookies } from '@/services/bizUserService';

const loginSrc = import.meta.env.VITE_LOGIN_SRC;

export const UserType = {
  user: 0, // 正常用户
  guest: 1, // 游客
};

export const DEFAULT_AVATAR = 'https://p3.ssl.qhimg.com/t110b9a9301f857990b959170bb.png';

class Store {
  /**
   * userInfo : {
   *    uid : "",
   *    mobile : "152****2099",
   *    icon : "",
   *    nickname : "",
   *    addtime : "",
   * }
   */
  userInfo = null;
  isError = false; // 加载用户信息出错
  QHPassReady = false;
  isReady = false;
  isCompanyUser = false;

  bizLoginInfo = null;

  // 是否为登录用户
  get isUser() {
    return !!this.userInfo?.id;
  }

  // 是否为企业用户
  get isBrand() {
    return !!this.userInfo?.brand?.tenant_id;
  }

  // uid
  get uid() {
    return this.userInfo?.id || '';
  }

  /**
   * 优先取用户头像
   * 没有使用默认头像
   */
  get showIcon() {
    const userCenterAvatar = (this.userInfo?.avatar || '').replace('http:', 'https:');
    return userCenterAvatar || Conf.defaultUserAvatar;
  }
  get showUserInfo() {
    return {
      ...this.userInfo,
      ...{
        icon: this.showIcon,
        nickname: '我',
      },
    };
  }
  get showNickname() {
    return this.userInfo?.displayname || this.userInfo?.username || '';
  }

  /**
   * 是否有上传证件照的权限(用户已许可)
   */
  get hasUploadAuth() {
    // debug
    // return false;

    return this.privacyData.credential_photo == 1;
  }

  /**
   * 黑白名单用户
   */
  isWhite = false;
  isBlack = false;
  /**
   * Vip 用户
   * 可切换 Dark 模式，多模型协作可见等
   */
  isVip = false;

  membershipInfo = {};
  coinBalance = {};
  membershipUsage = [];

  constructor() {
    makeAutoObservable(this);

    this.initUserInfo();
    this.getMembershipInfo();
    this.getCoinBalance();
    this.getMembershipUsage();
    /**
     * 客户端登录状态改变
     */
    EE.on('reloadUserInfo', this.requestUserInfo);

    EE.on('logout', config => {
      // 排查请求死循环问题
      slsLogger.log({
        action: 'debug',
        ext: 'logout',
        ext1: config?.from || '',
        ext2: this.uid,
      });

      if (client.isLLQ && this.userInfo) {
        this.initUserInfo();
      }
    });

    if (!HelperCore.IS_PROD) {
      window._userStore = this;
    }
  }

  initUserInfo() {
    const handleGetUserInfo = () => {
      this.requestUserInfo().then(() => {
        if (this.userInfo) {
          EE.emit('login', this.userInfo);

          this.checkUserStatus();
          this.checkKnowledgeStatus();
        }

        if (this.needGotoHome()) {
          setTimeout(() => {
            globalStore.gotoHome();
          }, 20);
        }
      });
    };
    getLoginInfo()
      .then(loginInfo => {
        const Q = loginInfo.data?.userInfo?.Q;
        const T = loginInfo.data?.userInfo?.T;
        if (Q && T) {
          runInAction(() => {
            this.bizLoginInfo = loginInfo.data;
          });
          cookie.set('Q', Q, {
            domain: 'n.cn',
            sameSite: 'None',
            path: '/',
            secure: true,
          });
          cookie.set('T', T, {
            domain: 'n.cn',
            sameSite: 'None',
            path: '/',
            secure: true,
          });
          return refreshUserCookies(Q, T, loginInfo.data.pin, loginInfo.data.token).catch(() =>
            Promise.resolve(true)
          );
        } else {
          return Promise.reject('Q or T is null');
        }
      })
      .then(() => {
        return handleGetUserInfo();
      })
      .finally(() => {
        runInAction(() => {
          this.isReady = true;
        });
      });
  }

  /**
   * 退出登录
   */
  logout({ needConfirm = true, cb } = {}) {
    if (!needConfirm) {
      this.doLogout();
    } else {
      Other.confirm({
        title: '温馨提示',
        content: '确认要退出账号吗？',
        okText: '退出',
        onOk: () => {
          cb && cb();
          this.doLogout();
        },
      });
    }
  }
  doLogout() {
    sendBroadcast('user-status-changed');
    setTimeout(() => {
      window &&
        window.QHPass.signOut(true, () => {
          globalStore.reloadApp();
        });
    }, 50);
  }

  getSafeCloudLoginInfo() {
    getLoginInfo().then(loginInfo => {
      if (loginInfo?.data?.userInfo) {
        runInAction(() => {
          const userInfo = loginInfo.data.userInfo || {};
          if (userInfo.Q && userInfo.T) {
            this.bizLoginInfo = loginInfo.data;
          }
        });
      }
    });
  }

  /**
   * 已登录返回登录用户信息
   * 未登录返回游客信息
   */
  requestUserInfo = () => {
    this.isError = false;
    return request
      .get('/user/info')
      .then(res => {
        runInAction(() => {
          this.userInfo = res.data;
          this.isVip =
            this.userInfo?.level == 1 ||
            !!localStorage.getItem('isvip') ||
            import.meta.env.MODE === 'dev';
          // 获取用户密保信息, 用来判断用户是否绑定手机号码和密保邮箱信息
          this.getUserSecInfo();
        });

        this.updateUserLevel();
        this.updateUserStatus();
        sendBroadcast('user-status-changed', res.data);
      })
      .catch(res => {
        runInAction(() => {
          this.isError = true;
        });
      })
      .finally(() => {
        // 非白名单用户
        if (!this.isVip) {
          // 不支持 Dark 模式
          if (globalStore.isDark) {
            globalStore.setUserConfig({
              theme: 'light',
            });
          }
          // 不支持多模型协作、组队较量
          globalStore.whenCloudReady(() => {
            if (
              /^\/(cooperation|teampk)/.test(location.pathname) &&
              globalStore.cloudConfig.webConfig?.support_cooperation == 0
            ) {
              globalStore.reloadApp();
            }
          });
        }

        // 不支持 Dark 模式的页面
        if (globalStore.isDark && /^\/(login)/.test(location.pathname)) {
          globalStore.setUserConfig({
            theme: 'light',
          });
        }
        runInAction(() => {
          this.isReady = true;
        });

        if (!this.isUser) {
          // 在大模型页，跳大模型首页
          // 其它跳首页

          // 智能体分享页&share=1，未登录时，跳转
          if (
            location.pathname.startsWith('/tools/aiagent/chat/') &&
            location.search.includes('share=1')
          ) {
            location.href = `/tools/aiagent/share?agentid=${location.pathname.split('/').pop()}`;
            return;
          }

          // 360se浏览器侧边栏，智能体首页，不需要登录
          if (client.isSeLLQ) {
            if (location.pathname == '/tools/aiagent' || location.pathname == '/tools/aiagent/') {
              return;
            } else if (location.pathname.startsWith('/tools/aiagent/chat/')) {
              location.replace(HelperCore.repaireUrl('/tools/aiagent'));
              return;
            }
          }

          // 不需要登录的页面
          if (
            location.pathname.startsWith('/tools/aiagent/share') ||
            location.pathname === '/chathome' ||
            location.pathname === '/' ||
            !this.needLogin()
          ) {
            return;
          }

          if (location.pathname === '/teamhome') {
            return;
          }

          location.replace('/teamhome');
          return;
        }

        globalStore.whenCloudReady(() => {
          this.initQaSDK();
        });
      });
  };

  /**
   * 获取当前登录帐号密保信息
   * 用来判断用户是否绑定手机号码和密保邮箱信息。
   * @param {*} callback
   */
  getUserSecInfo(callback) {
    if (window.QHPass) {
      this.initQHPass(() => {
        QHPass.getUserSecInfo(data => {
          const { relMobileNumber, safeSecEmail, safeSecMobile } = data || {};
          runInAction(() => {
            this.userInfo = {
              ...this.userInfo,
              relMobileNumber,
              safeSecEmail,
              safeSecMobile,
            };
          });
          callback && callback();
        });
      });
    }
  }

  /**
   * 初始化时，如果未登录，跳首页
   */
  needLogin() {
    if (
      [
        // 自行处理登录
        '/tools/knowledge',
      ].includes(location.pathname)
    ) {
      return false;
    }

    return (
      [].includes(location.pathname) ||
      /^\/(tools|agentchat|aichat|search|chat|teamhome)/.test(location.pathname)
    );
  }

  /**
   * 初始化时，需要跳首页的页面
   */
  needGotoHome() {
    return location.pathname == '/chat'; // 上一次 cid 尚未生成
  }

  /**
   * 用户等级
   * 黑白名单
   */
  updateUserLevel() {
    request
      .get('/user/setting')
      .then(res => {
        // debug mock 非白非黑
        // return;

        const { data } = res;
        runInAction(() => {
          this.isWhite = data.auth_level == 1;
          this.isBlack = data.auth_level == -1; // TODO 待定
        });
      })
      .catch(() => {})
      .finally(() => {
        EE.emit('user-white-black');
      });
  }

  /**
   * 是否内部员工
   */
  updateUserStatus() {
    request
      .get(Loader.getAISoUri('/user/detail/info'))
      .then(res => {
        const { data } = res;
        runInAction(() => {
          this.isCompanyUser = data.location_level == 1;
        });
      })
      .catch(() => {});
  }

  /**
   * 去登录
   * 浏览器中会使用
   */
  gotoLogin = (callback = true, origin, internalLogin = false, isLoginCb = null) => {
    if (client.isClient) {
      client.run('ShowLogin');
      return;
    }
    if ((client.isLLQ || client.is360se) && !internalLogin) {
      client.IsLogin().then(IsLogin => {
        if (IsLogin) {
          if (isLoginCb) {
            isLoginCb();
            return;
          }
          if (origin) {
            client.SyncLoginCookie(origin);
            globalStore.reloadPage();
          } else {
            this.initUserInfo();
          }
        } else {
          client.setData('loginedCallback', callback);
          client.run('ReloginByManual', undefined, 'config', '', 'loginenrol');
        }
      });
      return;
    }
    if (globalStore.globalConfig.loginBySDK) {
      this.initQHPass(() => {
        QHPass.signIn(userInfo => {
          setTimeout(() => {
            this.getCoinBalance();
            this.getMembershipInfo();
            this.getMembershipUsage();
            const type = typeof callback;
            if (callback === true) {
              globalStore.reloadPage();
            } else if (type == 'string') {
              globalStore.hrefNavigate(callback, true);
            } else if (type == 'function') {
              this.requestUserInfo();
              callback();
            }
          }, 30);
        });
      });
    } else {
      const params = {
        src: loginSrc,
        destUrl: location.href,
      };

      location.href = `https://${Conf.loginDomain}/login?${HelperCore.param(params)}`;
    }
  };

  doWhenLogin = cb => {
    if (this.isUser) {
      cb?.();
      return;
    }
    if (client.isLLQ) {
      client.IsLogin().then(IsLogin => {
        if (IsLogin) {
          this.initUserInfo();
          cb?.();
        } else {
          message.info('请先登录');
          this.gotoLogin();
        }
      });
      return;
    }
    message.info('请先登录');
    this.gotoLogin();
  };

  /**
   * 报给推推机器人
   * @param {String} message
   */
  reportToNRobot(message) {
    const content = [
      Conf.appName,
      `route: ${location.href}`,
      `user: ${this.uid} ${this.userInfo?.username}`,
      `revision: ${JSON.stringify(HelperCore.revision)}`,
      `ua: ${navigator.userAgent}`,
      `message: ${message}`,
    ].join('\n');

    reportToNRobot(content);
  }

  /**
   * 登录后，检查用户状态
   */
  checkUserStatus() {
    // 同意协议弹窗
    if (!/\/(agreements|privacy|knowledge\/chat|tools|static)/.test(location.pathname)) {
      Loader.getPrivacyRole('login').then(isOk => {
        if (!isOk && typeof isOk == 'boolean') {
          // 隐私弹窗
          AgreeModal.show({
            onSucc: async () => {},
          });
        }
      });
    }
  }

  /**
   * 自动创建知识库目录
   */
  checkKnowledgeStatus() {
    globalStore.whenCloudReady(() => {
      const currentId = globalStore.cloudConfig.assistantKnowledgeAutofolder?.id;
      if (!currentId) {
        return;
      }

      const localKey = `assistant_knowledge_autofolder`;
      const localData = Local.getItem(localKey) || {};
      if (localData[this.uid] != currentId) {
        request.post('/knw_cloud/inRouter/v1/knowledge/cloud/user/info').then(res => {
          localData[this.uid] = currentId;
          Local.setItem(localKey, localData);
        });
      }
    });
  }

  /**
   * 用户中心登录
   */
  initQHPass(callback) {
    if (this.QHPassReady) {
      callback && callback();
      return;
    }
    const opts = {
      src: loginSrc,
      charset: document.charset || document.defaultCharset || document.characterSet || 'UTF-8',
      domainList: ['360.com', 'sou.com', 'so.com', '360.cn', 'n.cn'],
      protocol: location.protocol.replace(':', ''),
      proxy: location.protocol + '//' + location.host + '/psp_jump.html',
      ignoreCookie: false,
      useMonitor: true, // 当为HTTPS时，关闭统计
      headSize: '100_100',
      bindMobile: {
        isRelMobile: 0, //是否显示关联手机号 1:显示 0:不显示 默认：0
      },
      primaryColor: '#2780FE',
      signIn: {
        types: ['mobile', 'normal'],
        panelLogo: '',
        normal: { panelTitle: '账号登录' },
        thirdPart: { providers: [] },
        autoreg: 1,
        agreement: true,
        protocolUrl: '/agreements/protocol',
        privacyUrl: '/agreements/privacy',
      },
    };
    if (window.QHPass) {
      window.QHPass && QHPass.init(opts);
      this.QHPassReady = true;
      callback && callback();
    } else {
      slsLogger.log({
        action: 'quc',
        arrt: 'notloaded',
        ext: typeof callback,
      });
      setTimeout(() => {
        runInAction(() => {
          this.initQHPass(callback);
        });
      }, 1000);
    }
  }
  async initQaSDK() {
    // 0 全部不支持  1 全部支持  2 仅web端支持 3 仅客户端支持
    const qasdkVal = globalStore.cloudConfig?.webConfig?.support_res_qasdk;
    if (qasdkVal == '0') return;
    if (client.isLLQ) {
      if (qasdkVal == '2') return;
    } else {
      if (qasdkVal == '3') return;
    }
    try {
      const loadSdk = await loadModule('qaSdk').catch(err => false);
      if (!loadSdk) {
        return;
      }
      // 问卷初始化回调函数，initQaSDK 是异步的，若想调用其他方法，务必要在initQaSDK执行完成后操作
      window.qaSdk = QaSdk.initQaSDK({
        isAutoDisplay: false,
        appId: '66bc80cd9d9c45f67077979e',
        position: 'rightBottom', //问卷在页面中出现的位置
        level: 10000, // z-index
        singleDisplay: true,
        userInfo: { uid: this.uid || HelperCore.getMid() },
        callback: success => {
          HelperCore.log('初始化--->', success);
        },
      });
    } catch (error) {
      HelperCore.log('initQaSDK error', error);
    }
  }
  async qaSDKTrigger(params) {
    HelperCore.log(params);
    //只【AI搜索】【超级搜索】出示问卷
    if (!/^(\/aichat|\/sosearch)/.test(location.pathname)) return;
    // 0 全部不支持  1 全部支持  2 仅web端支持 3 仅客户端支持
    const qasdkVal = globalStore.cloudConfig?.webConfig?.support_res_qasdk;
    if (qasdkVal == '0') return;
    if (client.isLLQ) {
      if (qasdkVal == '2') return;
    } else {
      if (qasdkVal == '3') return;
    }
    //提问三次之后弹出问卷
    const key = this.uid || HelperCore.getMid();
    const cacheQaSDKData = Local.getItem('QASKD_COUNT') || {};
    const count = cacheQaSDKData[key] || 0;
    if (count >= 2) {
      if (!window.qaSdk) {
        await this.initQaSDK();
      }
      qaSdk.eventTrigger({
        eventCode: 'cymeddss69kxon',
        eventAttrs: {
          key,
          u: window.location.href,
          src: HelperCore.getQueryString('src'),
          ...params,
        },
        onCompleted: data => {
          HelperCore.log('触发事件成功', data);
        },
      });
    }
    cacheQaSDKData[key] = count + 1;
    Local.setItem('QASKD_COUNT', cacheQaSDKData);
  }

  // 绑定手机号
  showBindMobile(callback) {
    if (window.QHPass) {
      this.initQHPass(() => {
        QHPass.bindMobile(callback);
      });
    }
  }

  getMembershipInfo() {
    request
      .get('/membership/info?appsource=so')
      .then(res => {
        runInAction(() => {
          this.membershipInfo = res.data || {};
        });
        client.run('SetMembershipInfo', () => {}, res.data || {});
        console.log('SetMembershipInfo', res.data);
      })
      .catch(() => {});
  }

  getCoinBalance() {
    request
      .get('/user/coin/balance?appsource=so')
      .then(res => {
        runInAction(() => {
          this.coinBalance = res.data || {};
        });
        client.run('SetNamiInfo', () => {}, res.data || {});
      })
      .catch(() => {});
  }

  getMembershipUsage() {
    request
      .get('/membership/usage?appsource=so', {
        params: {
          func_ids:
            'advanced_model,parallel_tasks,limit_reached,balance_insufficient,agent_count_limit',
        },
      })
      .then(res => {
        runInAction(() => {
          this.membershipUsage = res.data || [];
        });
      })
      .catch(() => {});
  }
}

const userStore = new Store();
export default userStore;
